import { Tooltip } from 'antd';
import { PageHeader, TWDataTable } from '../../components/ui';
import {
  ArrowDownTrayIcon,
  CheckBadgeIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { SkeletonBlock } from '../../components/skeleton/skeleton';
import termAcceptanceApi from '../../services/termAcceptanceApi';
import { headerCls } from '../../data/columns';
import './term-acceptances.css';

const AVATAR_GRADIENTS = [
  'from-sky-500 to-blue-600',
  'from-violet-500 to-indigo-600',
  'from-emerald-500 to-teal-600',
  'from-amber-500 to-orange-600',
  'from-rose-500 to-pink-600',
  'from-fuchsia-500 to-purple-600',
];

const hashToGradient = (value) => {
  if (!value) return AVATAR_GRADIENTS[0];
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
};

const initialsOf = (name, fallback) => {
  const source = (name || fallback || '?').trim();
  if (!source) return '?';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const TermAcceptances = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalRecords: 0,
  });

  const fetchAcceptances = async (page = pagination.pageNumber) => {
    setLoading(true);
    try {
      const res = await termAcceptanceApi.list({
        PageNumber: page,
        PageSize: pagination.pageSize,
      });
      setData(res.data?.data ?? []);
      setPagination((prev) => ({
        ...prev,
        pageNumber: page,
        totalRecords: res.data?.totalRecords ?? 0,
      }));
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = (item) => {
    const html = item.merchanttermhtml;
    if (!html) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const docId = item.documentid || '';
    const acceptedFormatted = item.acceptedat
      ? moment(item.acceptedat).format('MM/DD/YYYY hh:mm A')
      : '-';
    const ipAddress = item.useripaddressv4 || item.useripaddressv6 || '-';
    const version = item.term?.version != null ? 'v' + item.term.version : '-';

    doc.querySelectorAll('h1 > br, h2 > br').forEach((br) => {
      const parent = br.parentElement;
      if (parent && parent.textContent.trim() === '') {
        parent.style.margin = '0';
        parent.style.padding = '0';
        parent.style.lineHeight = '0.5';
      }
    });

    const metaDiv = doc.createElement('div');
    metaDiv.style.marginTop = '24px';
    metaDiv.style.borderTop = '1px solid #999';
    metaDiv.style.paddingTop = '12px';
    metaDiv.innerHTML =
      '<p><strong>Document ID:</strong> ' +
      docId +
      '</p>' +
      '<p><strong>Acceptance Date:</strong> ' +
      acceptedFormatted +
      '</p>' +
      '<p><strong>IP Address:</strong> ' +
      ipAddress +
      '</p>' +
      '<p><strong>Agreement Version:</strong> ' +
      version +
      '</p>';
    doc.body.appendChild(metaDiv);

    const userName = item.user?.fullname || item.firstname || 'Merchant';
    const companyName = item.company?.name || 'Agreement';
    doc.title = userName + ' - ' + companyName + ' - Merchant Agreement';

    const printStyle = doc.createElement('style');
    printStyle.textContent =
      '@page { size: A4; margin: 20mm 20mm 18mm 20mm; }' +
      'body { margin: 0; padding: 0; font-size: 9.5px !important; }' +
      'h1 { font-size: 16px !important; }' +
      'h2 { font-size: 12px !important; }' +
      '.print-wrapper { width: 100%; border-collapse: collapse; }' +
      '.print-wrapper thead td, .print-wrapper tfoot td { padding: 0; }' +
      '.print-header { text-align: right; font-size: 8px; color: #888; padding-bottom: 2mm; }' +
      '.print-footer { font-size: 8px; color: #888; padding-top: 2mm; border-top: 0.5px solid #ccc; }' +
      'h1, h2 { page-break-after: avoid; }' +
      'p, li { orphans: 3; widows: 3; }' +
      'ol, ul { page-break-inside: avoid; }';
    doc.head.appendChild(printStyle);

    const companyId = item.companyid || item.company?.id || '';
    const bodyContent = doc.body.innerHTML;
    doc.body.innerHTML =
      '<table class="print-wrapper"><thead><tr><td>' +
      '<div class="print-header">' +
      docId +
      '</div>' +
      '</td></tr></thead>' +
      '<tfoot><tr><td>' +
      '<div class="print-footer">' +
      '<span>' +
      companyId +
      '</span>' +
      '</div>' +
      '</td></tr></tfoot>' +
      '<tbody><tr><td>' +
      bodyContent +
      '</td></tr></tbody></table>';

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(doc.documentElement.outerHTML);
    printWindow.document.close();
    printWindow.addEventListener('load', () => {
      printWindow.focus();
      printWindow.print();
    });
  };

  useEffect(() => {
    fetchAcceptances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredData = useMemo(() => {
    if (!search.trim()) return data;
    const needle = search.trim().toLowerCase();
    return data.filter((item) => {
      const fields = [
        item.user?.fullname,
        item.user?.email,
        item.company?.name,
        item.documentid,
        item.useripaddressv4,
        item.useripaddressv6,
      ];
      return fields.some((f) =>
        (f || '').toString().toLowerCase().includes(needle),
      );
    });
  }, [data, search]);

  const showPagination =
    pagination.totalRecords > pagination.pageSize && !search.trim();

  const columns = [
    {
      title: <span className={headerCls}>User</span>,
      key: 'user',
      render: (_, item) => {
        const userName = item.user?.fullname || '—';
        const userEmail = item.user?.email || '';
        const gradient = hashToGradient(userEmail || userName);
        const initials = initialsOf(userName, userEmail);
        return (
          <div className="flex min-w-0 items-center gap-3">
            <div
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br ${gradient} text-[0.7rem] font-semibold text-white shadow-sm`}
            >
              {initials}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
                {userName}
              </p>
              {userEmail && (
                <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                  {userEmail}
                </p>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: <span className={headerCls}>Company</span>,
      key: 'company',
      render: (_, item) => (
        <span className="text-sm text-slate-700 dark:text-slate-200">
          {item.company?.name || '—'}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Version</span>,
      key: 'version',
      render: (_, item) =>
        item.term?.version != null ? (
          <span className="inline-flex h-6 items-center rounded-md bg-blue-50 px-2 text-xs font-semibold text-blue-700 dark:bg-sky-500/10 dark:text-sky-300">
            v{item.term.version}
          </span>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        ),
    },
    {
      title: <span className={headerCls}>Document ID</span>,
      key: 'documentid',
      render: (_, item) => (
        <span
          className="inline-block max-w-full truncate rounded-md bg-slate-100 px-2 py-1 font-mono text-xs text-slate-700 dark:bg-slate-700/60 dark:text-slate-200"
          title={item.documentid || ''}
        >
          {item.documentid || '—'}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>IP Address</span>,
      key: 'ip',
      render: (_, item) => (
        <span className="font-mono text-xs text-slate-600 dark:text-slate-400">
          {item.useripaddressv4 || item.useripaddressv6 || '—'}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Accepted At</span>,
      key: 'acceptedat',
      render: (_, item) => (
        <span className="text-xs text-slate-600 dark:text-slate-400">
          {item.acceptedat
            ? moment(item.acceptedat).format('MM/DD/YYYY hh:mm A')
            : '—'}
        </span>
      ),
    },
    {
      title: <span className="sr-only">Actions</span>,
      key: 'actions',
      align: 'right',
      width: 60,
      render: (_, item) => {
        const canDownload = !!item.merchanttermhtml;
        return (
          <Tooltip
            title={canDownload ? 'Download PDF' : 'No document available'}
          >
            <button
              type="button"
              disabled={!canDownload}
              onClick={() => handleDownloadPdf(item)}
              className={`inline-flex h-9 w-9 items-center justify-center rounded-full transition ${
                canDownload
                  ? 'text-blue-600 hover:bg-blue-50 dark:text-sky-400 dark:hover:bg-sky-400/10'
                  : 'cursor-not-allowed text-slate-300 dark:text-slate-600'
              }`}
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              <span className="sr-only">Download</span>
            </button>
          </Tooltip>
        );
      },
    },
  ];

  const TermAcceptancesSkeleton = () => (
    <div className="reveal" aria-hidden="true">
      <table className="min-w-full divide-y divide-gray-300 dark:divide-slate-500">
        <thead>
          <tr>
            <th className="py-3.5 pr-3 text-left text-sm font-semibold text-slate-900 dark:text-slate-100">
              User
            </th>
            <th className="py-3.5 px-4 text-left text-sm font-semibold text-slate-900 dark:text-slate-100">
              Company
            </th>
            <th className="py-3.5 px-4 text-left text-sm font-semibold text-slate-900 dark:text-slate-100">
              Version
            </th>
            <th className="py-3.5 px-4 text-left text-sm font-semibold text-slate-900 dark:text-slate-100">
              Document ID
            </th>
            <th className="py-3.5 px-4 text-left text-sm font-semibold text-slate-900 dark:text-slate-100">
              IP Address
            </th>
            <th className="py-3.5 px-4 text-left text-sm font-semibold text-slate-900 dark:text-slate-100">
              Accepted At
            </th>
            <th className="py-3.5 px-4 text-right">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-slate-500">
          {[...Array(8)].map((_, i) => (
            <tr key={i}>
              <td className="whitespace-nowrap py-4 pr-3">
                <div className="flex items-center gap-3">
                  <SkeletonBlock className="h-9 w-9 !rounded-full" />
                  <div className="space-y-2">
                    <SkeletonBlock className="h-4 w-28" />
                    <SkeletonBlock className="h-3 w-36" />
                  </div>
                </div>
              </td>
              <td className="whitespace-nowrap px-4 py-4">
                <SkeletonBlock className="h-4 w-28" />
              </td>
              <td className="whitespace-nowrap px-4 py-4">
                <SkeletonBlock className="h-6 w-10 !rounded-full" />
              </td>
              <td className="whitespace-nowrap px-4 py-4">
                <SkeletonBlock className="h-4 w-32" />
              </td>
              <td className="whitespace-nowrap px-4 py-4">
                <SkeletonBlock className="h-4 w-24" />
              </td>
              <td className="whitespace-nowrap px-4 py-4">
                <SkeletonBlock className="h-4 w-28" />
              </td>
              <td className="whitespace-nowrap px-4 py-4 text-right">
                <SkeletonBlock className="ml-auto h-8 w-8 !rounded-full" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="acceptances-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto w-full max-w-7xl space-y-6">
        <PageHeader
          title="Term Acceptances"
          description="A list of all merchant agreement acceptances with audit trail."
          actions={
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search user, company, IP…"
                className="h-10 w-full rounded-full border border-slate-200 bg-white pl-9 pr-4 text-sm text-slate-700 placeholder:text-slate-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:placeholder:text-slate-500 dark:focus:border-sky-400 dark:focus:ring-sky-400 sm:w-72"
              />
            </div>
          }
        />

        {/* Summary chip */}
        <div className="reveal flex flex-wrap items-center gap-2 text-xs font-medium text-slate-600 dark:text-slate-300">
          <span className="inline-flex h-7 items-center gap-1.5 rounded-md bg-blue-50 px-3 text-blue-700 dark:bg-sky-500/10 dark:text-sky-300">
            <CheckBadgeIcon className="h-3.5 w-3.5" />
            {pagination.totalRecords || data.length} total
          </span>
          {search.trim() && (
            <span className="inline-flex h-7 items-center rounded-md bg-slate-100 px-3 text-slate-600 dark:bg-slate-700/60 dark:text-slate-200">
              {filteredData.length} match
              {filteredData.length === 1 ? '' : 'es'}
            </span>
          )}
        </div>

        {loading ? (
          <TermAcceptancesSkeleton />
        ) : filteredData.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-md bg-slate-100 text-slate-400 dark:bg-slate-700/60 dark:text-slate-500">
              <CheckBadgeIcon className="h-7 w-7" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                {search.trim()
                  ? 'No acceptances match your search.'
                  : 'No term acceptances yet.'}
              </p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {search.trim()
                  ? 'Try a different user, company or IP.'
                  : 'Acceptances will appear here once merchants agree.'}
              </p>
            </div>
          </div>
        ) : (
          <div className="reveal">
            <TWDataTable
              columns={columns}
              data={filteredData}
              rowKey="id"
              pagination={
                showPagination
                  ? {
                      current: pagination.pageNumber,
                      pageSize: pagination.pageSize,
                      total: pagination.totalRecords,
                      showTotal: (total, range) =>
                        `Showing ${range[0]} to ${range[1]} of ${total} acceptances`,
                      onChange: (page) => fetchAcceptances(page),
                      showSizeChanger: false,
                    }
                  : undefined
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TermAcceptances;
